import { AvatarBox } from "@/components/logo";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import { Button, Heading, Text } from "@medusajs/ui";
import sdk, { type SalonStockerHttpTypes } from "@repo/api-sdk";
import { Link, createRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { rootRoute } from "../$rootRoute";

/**
 * The Supplier Match confirmation-link route (ADR-0005).
 *
 * A **public** sibling of `enter`/`login`/`reset-password`, deliberately
 * outside `authRoute`: the thing being proven is mailbox ownership, not
 * SalonStocker-account identity, and the person confirming may not be the
 * salon's usual logged-in user.
 */
export const supplierMatchConfirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "supplier-match/confirm",
  validateSearch: (search?: Record<string, unknown>): { token?: string } => ({
    token: typeof search?.token === "string" ? search.token : undefined
  }),
  component: SupplierMatchConfirmPage
});

type ConfirmState = "confirming" | "confirmed" | "failed" | "missing-token";

/**
 * Copy for a link that cannot be confirmed. Says "ask for a fresh link",
 * never "unauthorized" - the salon has done nothing wrong (ADR-0005's own
 * route doc comment: both an unknown and an expired token mean the same
 * thing to a salon).
 */
const FALLBACK_FAILURE_MESSAGE =
  "This confirmation link is not valid. Request a fresh one from the Supplier Match page.";

function SupplierMatchConfirmPage() {
  const { token } = useSearch({ from: supplierMatchConfirmRoute.id });

  const [state, setState] = useState<ConfirmState>(
    token ? "confirming" : "missing-token"
  );
  const [message, setMessage] = useState(FALLBACK_FAILURE_MESSAGE);
  const [result, setResult] =
    useState<SalonStockerHttpTypes.StoreConfirmSupplierMatchEmailResponse | null>(
      null
    );

  // One confirmation per mounted page. Deliberately NOT paired with a
  // cancellation flag - see EnterPage.tsx's identical fire-once pattern for
  // why that would strand StrictMode's double-invoke in a stuck state.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) {
      return;
    }
    startedRef.current = true;

    // Deliberately a POST fired from here, on mount - never on GET of this
    // URL. An email security gateway (Defender Safe Links, Proofpoint)
    // prefetches the emailed link's HTML to scan it but does not execute JS,
    // so a GET-burned token would be dead before the salon ever clicked it.
    const confirm = async () => {
      let response: SalonStockerHttpTypes.StoreConfirmSupplierMatchEmailResponse;
      try {
        response = await sdk.store.supplierMatch.confirmEmail(token);
      } catch (error) {
        // A 4xx is the server's verdict on the link (expired, already used) and
        // is the message to show; a 5xx or no answer is ours to report.
        setMessage(
          handleActionError(error, {
            boundary: "supplier match confirm",
            fallback: FALLBACK_FAILURE_MESSAGE,
            trustClientErrors: true
          })
        );
        setState("failed");
        return;
      }

      setResult(response);
      setState("confirmed");
    };

    void confirm();
  }, [token]);

  return (
    <div className="bg-ui-bg-subtle flex min-h-dvh w-dvw items-center justify-center">
      <div className="m-4 flex w-full max-w-[320px] flex-col items-center text-center">
        <AvatarBox checked={false} />

        {state === "confirming" ? (
          <div className="flex flex-col items-center gap-y-1">
            <Heading>Confirming your email…</Heading>
            <Text size="small" className="text-ui-fg-subtle">
              One moment.
            </Text>
          </div>
        ) : state === "confirmed" ? (
          <div className="flex w-full flex-col items-center gap-y-4">
            <div className="flex flex-col items-center gap-y-1">
              <Heading>Email confirmed</Heading>
              <Text size="small" className="text-ui-fg-subtle">
                <span className={PII_MASK_CLASS}>{result?.verified_email}</span>{" "}
                is now confirmed.
              </Text>
            </div>
            <Link to="/account/supplier-match" className="w-full">
              <Button className="w-full" variant="primary">
                Go to Supplier Match
              </Button>
            </Link>
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-y-4">
            <div className="flex flex-col items-center gap-y-1">
              <Heading>This link is no longer valid</Heading>
              <Text size="small" className="text-ui-fg-subtle">
                {state === "missing-token"
                  ? "This link is missing its code. Request a fresh one from the Supplier Match page."
                  : message}
              </Text>
            </div>
            <Link to="/login" className="w-full">
              <Button className="w-full" variant="primary">
                Sign in
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
